import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { config } from '../config/environment';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Paper,
  Chip,
  Fade,
  Slide,
} from '@mui/material';
import {
  QrCodeIcon,
  UserIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';

// Use static import as lazy load does not prevent automatic camera activation but causes vite warnings
import { Scanner } from '@yudiel/react-qr-scanner';
import MobileCameraHandler from '../components/common/MobileCameraHandler';
import { MainLogo } from '../components/common/CompanyLogo';

interface Worker {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  skills: string[];
  role: string;
  branch_id: string;
  branch_name?: string;
}

const SupervisorLogin: React.FC = () => {
  const navigate = useNavigate();

  const [scanning, setScanning] = useState(false);

  // Debug: Log scanning state changes
  useEffect(() => {
    console.log('WorkerLoginPage: Scanning state changed to:', scanning);
  }, [scanning]);


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [worker, setWorker] = useState<Worker | null>(null);
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Check if supervisor is already logged in
  useEffect(() => {
    const savedSupervisor = localStorage.getItem('supervisor');
    const savedToken = localStorage.getItem('supervisorToken');
    const loginTime = localStorage.getItem('supervisorLoginTime');

    if (savedSupervisor && savedToken && loginTime) {
      try {
        const supervisorData = JSON.parse(savedSupervisor);
        const loginTimestamp = parseInt(loginTime);
        const now = Date.now();
        const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours

        // Check if login is still valid (within 24 hours)
        if (now - loginTimestamp < oneDayInMs) {
          setWorker(supervisorData);
          setSuccess(true);
          // Redirect to supervisor dispatch app after a short delay
          setTimeout(() => {
            window.location.href = '/supervisor/';
          }, 1000);
        } else {
          // Session expired, clear storage
          localStorage.removeItem('supervisor');
          localStorage.removeItem('supervisorToken');
          localStorage.removeItem('supervisorLoginTime');
        }
      } catch (error) {
        console.error('Error parsing saved supervisor data:', error);
        localStorage.removeItem('supervisor');
        localStorage.removeItem('supervisorToken');
        localStorage.removeItem('supervisorLoginTime');
      }
    }
  }, [navigate]);

  const handleQRCodeScanned = async (decodedText: string) => {
    try {
      console.log('Processing QR code:', decodedText);
      setError(null);
      setScanning(false);
      setLoading(true);

      const encryptedUserId = decodedText.trim();

      if (!encryptedUserId) {
        throw new Error('Invalid QR code - no encrypted user ID found');
      }

      console.log('Sending worker login request with encrypted user ID:', encryptedUserId);

      const response = await axios.post(`${config.API_URL}/auth/worker/login`, {
        username: encryptedUserId,
        password: '',
        loginType: 2 // 2 for QR code login
      }, {
        timeout: 10000,
        withCredentials: true
      });

      console.log('Worker login response:', response);

      if (response.data?.success) {
        const workerInfo = response.data.data.user;
        setWorker(workerInfo);
        setSuccess(true);

        // Store supervisor data with timestamp
        localStorage.setItem('supervisor', JSON.stringify(workerInfo));
        localStorage.setItem('supervisorToken', response.data.data.token);
        localStorage.setItem('supervisorLoginTime', Date.now().toString());

        console.log('SupervisorLogin: Login successful, redirecting to dispatch');

        // Redirect to supervisor dispatch app after successful login
        setTimeout(() => {
          window.location.href = '/supervisor/';
        }, 1000);
      } else {
        throw new Error(response.data?.error?.message || 'Login failed');
      }
    } catch (err: any) {
      console.error('QR Code scan error:', err);
      // Extract error message from axios response
      let errorMessage = 'Failed to process QR code';
      if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      // Show toast notification
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-right',
        style: {
          background: '#ef4444',
          color: '#fff',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
      setScanning(true); // Restart scanning
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialsLogin = async () => {
    try {
      setError(null);
      setLoading(true);

      console.log('Attempting credentials worker login...');

      // Use main auth API for credentials login
      const response = await axios.post(`${config.API_URL}/auth/worker/login`, {
        username: credentials.email,
        password: credentials.password,
        loginType: 1 // 1 for email/password login
      }, {
        timeout: 10000,
        withCredentials: true
      });

      console.log('Credentials worker login response:', response);

      if (response.data?.success) {
        const workerInfo = response.data.data.user; // Main auth API returns data.data.user
        setWorker(workerInfo);
        setSuccess(true);

        // Store supervisor data with timestamp
        localStorage.setItem('supervisor', JSON.stringify(workerInfo));
        localStorage.setItem('supervisorToken', response.data.data.token);
        localStorage.setItem('supervisorLoginTime', Date.now().toString());

        console.log('SupervisorLogin: Credentials login successful, redirecting to dispatch');

        // Redirect to supervisor dispatch app after successful login
        setTimeout(() => {
          window.location.href = '/supervisor/';
        }, 1000);
      } else {
        throw new Error(response.data?.error?.message || 'Login failed');
      }
    } catch (err: any) {
      console.error('Credentials login error:', err);
      // Extract error message from axios response
      let errorMessage = 'Failed to login with credentials';
      if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      // Show toast notification
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-right',
        style: {
          background: '#ef4444',
          color: '#fff',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScan = (result: any[]) => {
    console.log('QR Scan result:', result);
    if (result && result.length > 0) {
      const qrData = result[0]?.rawValue || result[0];
      console.log('QR Data received:', qrData);
      handleQRCodeScanned(qrData);
    }
  };

  const handleError = (error: any) => {
    console.log('Scanner error (normal):', error);
  };

  const handleBack = () => {
    navigate('/');
  };

  // if (success && worker) {
  //   return (
  //     <Box
  //       sx={{
  //         minHeight: '100vh',
  //         background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  //         display: 'flex',
  //         alignItems: 'center',
  //         justifyContent: 'center',
  //         padding: 2
  //       }}
  //     >
  //       <motion.div
  //         initial={{ scale: 0.8, opacity: 0 }}
  //         animate={{ scale: 1, opacity: 1 }}
  //         transition={{ duration: 0.5 }}
  //       >
  //         <Card
  //           sx={{
  //             maxWidth: 500,
  //             width: '100%',
  //             textAlign: 'center',
  //             borderRadius: 3,
  //             boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  //             overflow: 'hidden'
  //           }}
  //         >
  //           <CardContent sx={{ p: 4 }}>
  //             <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
  //             <Typography variant="h4" color="success.main" gutterBottom fontWeight="bold">
  //               Login Successful!~~~~
  //             </Typography>
  //             <Typography variant="h6" color="text.secondary" gutterBottom>
  //               Welcome , {worker.first_name} {worker.last_name} 
  //             </Typography>
  //             <Typography variant="body2" color="text.secondary" gutterBottom>
  //               Role: {worker.role} | Branch: {worker.branch_name || 'Main Branch'}
  //             </Typography>

  //             {worker.skills && worker.skills.length > 0 && (
  //               <Box mt={3}>
  //                 <Typography variant="subtitle2" gutterBottom>
  //                   Available Skills:
  //                 </Typography>
  //                 <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center">
  //                   {worker.skills.map((skill, index) => (
  //                     <Chip
  //                       key={index}
  //                       label={skill}
  //                       color="primary"
  //                       size="small"
  //                       sx={{ fontWeight: 500 }}
  //                     />
  //                   ))}
  //                 </Box>
  //               </Box>
  //             )}

  //             <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
  //               Redirecting to dashboard...
  //             </Typography>

  //             <CircularProgress sx={{ mt: 2 }} />
  //           </CardContent>
  //         </Card>
  //       </motion.div>
  //     </Box>
  //   );
  // }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex items-center justify-center min-h-screen w-full min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900"
    >

      {/* Glass morphism effect */}
      <div className="absolute inset-0 bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl" />

      <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl py-8 px-6 sm:px-10 rounded-3xl border border-white/20 shadow-2xl">


        <motion.div
          variants={itemVariants}
          className="w-full max-w-md bg-white rounded-2xl shadow-4xl p-1 space-y-6"
        >


          <motion.div variants={itemVariants}>
            {/* Login Type Toggle */}
            <MainLogo />
            {/* <Box sx={{ mb: 3 }}>
          <Paper
            sx={{
              display: 'flex',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #e0e0e0'
            }}
          >
            <Button
              fullWidth
              variant={loginType === 'qr' ? 'contained' : 'text'}
              onClick={() => {
                setLoginType('qr');
                setScanning(false); // Don't start scanning automatically
              }}
              sx={{
                borderRadius: 0,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5
              }}
            >
              <QrCodeIcon className="h-4 w-4 mr-2" />
              QR Login
            </Button>

            <Button
              fullWidth
              variant={loginType === 'normal' ? 'contained' : 'text'}
              onClick={() => setLoginType('normal')}
              sx={{
                borderRadius: 0,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5
              }}
            >
              <UserIcon className="h-4 w-4 mr-2" />
              Normal Login
            </Button>
          </Paper>
        </Box>                      */}
          </motion.div>

          <form className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault(); // stop page reload
              handleCredentialsLogin();
            }}
          >
            <motion.div variants={itemVariants}>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your email"
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  required
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <AnimatePresence mode="wait">
                    {showPassword ? (
                      <motion.svg
                        key="eye-off"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </motion.svg>
                    ) : (
                      <motion.svg
                        key="eye"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </motion.svg>
                    )}
                  </AnimatePresence>
                </button>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                // onClick={handleCredentialsLogin}
                className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200`}
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key="text"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    Sign in
                  </motion.span>

                </AnimatePresence>
              </motion.button>
            </motion.div>
          </form>


        </motion.div>

      </div>
    </motion.div>

  );
};

export default SupervisorLogin;
